import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Cake, CheckCircle, XCircle, Clock, Euro, Users, 
  Building2, TrendingUp, Eye, Loader2, Search, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminBirthdayClubSection = () => {
  const [loading, setLoading] = useState(true);
  const [pendingClubs, setPendingClubs] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedClub, setSelectedClub] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    installation_price: '',
    admin_notes: ''
  });
  const [processing, setProcessing] = useState(false);

  const fetchPendingClubs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/admin/pending`);
      setPendingClubs(data.clubs);
    } catch (error) {
      console.error('Error fetching pending clubs:', error);
    }
  }, []);

  const fetchAllClubs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/admin/all`);
      setAllClubs(data.clubs);
    } catch (error) {
      console.error('Error fetching all clubs:', error);
    }
  }, []);

  const fetchCommissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/admin/commissions`);
      setCommissions(data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPendingClubs(), fetchAllClubs(), fetchCommissions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPendingClubs, fetchAllClubs, fetchCommissions]);

  const handleApprove = async (approved) => {
    if (!selectedClub) return;
    
    if (approved && !approvalForm.installation_price) {
      toast.error('Debes especificar el precio de instalación');
      return;
    }
    
    setProcessing(true);
    
    try {
      await axios.post(`${API}/birthday-club/admin/approve`, {
        club_id: selectedClub.id,
        approved,
        installation_price: approved ? parseFloat(approvalForm.installation_price) : null,
        admin_notes: approvalForm.admin_notes
      });
      
      toast.success(approved ? 'Club aprobado correctamente' : 'Club rechazado');
      setSelectedClub(null);
      setApprovalForm({ installation_price: '', admin_notes: '' });
      fetchPendingClubs();
      fetchAllClubs();
    } catch (error) {
      toast.error('Error al procesar la solicitud');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async (clubId) => {
    try {
      await axios.post(`${API}/birthday-club/admin/mark-paid/${clubId}`);
      toast.success('Pago registrado. Club activado.');
      fetchAllClubs();
    } catch (error) {
      toast.error('Error al registrar el pago');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg">
          <Cake className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Gestión Club VIP Cumpleaños</h2>
          <p className="text-sm text-muted-foreground">
            Aprueba solicitudes y gestiona comisiones
          </p>
        </div>
      </div>

      {/* Stats */}
      {commissions && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Clubs Activos</p>
            <p className="text-2xl font-bold text-pink-600">{commissions.global_totals.active_clubs}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Ventas Totales</p>
            <p className="text-2xl font-bold text-green-600">{commissions.global_totals.total_sales.toFixed(2)}€</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Comisiones Totales</p>
            <p className="text-2xl font-bold text-purple-600">{commissions.global_totals.total_commission.toFixed(2)}€</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Total Canjes</p>
            <p className="text-2xl font-bold text-blue-600">{commissions.global_totals.total_redemptions}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'pending', label: `Pendientes (${pendingClubs.length})`, icon: Clock },
          { id: 'all', label: 'Todos los Clubs', icon: Building2 },
          { id: 'commissions', label: 'Comisiones', icon: Euro }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingClubs.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-muted-foreground">No hay solicitudes pendientes</p>
            </div>
          ) : (
            pendingClubs.map((club) => (
              <div key={club.id} className="bg-card rounded-2xl p-6 border border-border/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{club.business_info?.businessName || 'Negocio'}</h3>
                        <p className="text-sm text-muted-foreground">{club.business_info?.email}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Contactos</p>
                        <p className="font-semibold text-lg">{club.contacts_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Plan</p>
                        <p className="font-semibold">{club.business_info?.subscriptionPlan || 'Free'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Oferta</p>
                        <p className="font-semibold">{club.discount_value}{club.discount_type === 'percentage' ? '%' : '€'} OFF</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fecha</p>
                        <p className="font-semibold text-sm">{new Date(club.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-sm font-medium">{club.offer_title}</p>
                      <p className="text-sm text-muted-foreground">{club.offer_description}</p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => setSelectedClub(club)}
                    className="ml-4 bg-gradient-to-r from-pink-500 to-purple-600"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Revisar
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* All Clubs Tab */}
      {activeTab === 'all' && (
        <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
          <div className="grid grid-cols-7 gap-4 p-4 bg-muted/50 text-sm font-medium">
            <div>Negocio</div>
            <div>Contactos</div>
            <div>Estado</div>
            <div>Instalación</div>
            <div>Ventas</div>
            <div>Comisión</div>
            <div>Acciones</div>
          </div>
          <div className="divide-y divide-border/50">
            {allClubs.map((club) => (
              <div key={club.id} className="grid grid-cols-7 gap-4 p-4 items-center">
                <div className="font-medium truncate">{club.business_name}</div>
                <div>{club.contacts_count}</div>
                <div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    club.status === 'active' ? 'bg-green-100 text-green-700' :
                    club.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                    club.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {club.status}
                  </span>
                </div>
                <div>
                  {club.installation_price ? `${club.installation_price}€` : '-'}
                  {club.status === 'approved' && !club.installation_paid && (
                    <span className="ml-1 text-xs text-yellow-600">(pendiente)</span>
                  )}
                </div>
                <div className="font-medium">{club.total_sales?.toFixed(2) || '0.00'}€</div>
                <div className="text-purple-600 font-medium">{club.total_commission?.toFixed(2) || '0.00'}€</div>
                <div>
                  {club.status === 'approved' && !club.installation_paid && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkPaid(club.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Marcar Pagado
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commissions Tab */}
      {activeTab === 'commissions' && commissions && (
        <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
          <div className="grid grid-cols-5 gap-4 p-4 bg-muted/50 text-sm font-medium">
            <div>Negocio</div>
            <div>Canjes</div>
            <div>Ventas Totales</div>
            <div>Comisión Total</div>
            <div>Media/Canje</div>
          </div>
          <div className="divide-y divide-border/50">
            {commissions.businesses.map((biz, i) => (
              <div key={i} className="grid grid-cols-5 gap-4 p-4 items-center">
                <div className="font-medium">{biz.business_name}</div>
                <div>{biz.redemptions_count}</div>
                <div className="font-medium">{biz.total_sales.toFixed(2)}€</div>
                <div className="text-purple-600 font-bold">{biz.total_commission.toFixed(2)}€</div>
                <div className="text-muted-foreground">
                  {biz.redemptions_count > 0 ? (biz.total_sales / biz.redemptions_count).toFixed(2) : '0.00'}€
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approval Modal */}
      <AnimatePresence>
        {selectedClub && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedClub(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 w-full max-w-lg shadow-xl"
            >
              <h3 className="text-xl font-bold mb-4">Aprobar Solicitud</h3>
              
              <div className="space-y-4 mb-6">
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="font-semibold">{selectedClub.business_info?.businessName}</p>
                  <p className="text-sm text-muted-foreground">{selectedClub.business_info?.email}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm"><strong>{selectedClub.contacts_count}</strong> contactos</span>
                    <span className="text-sm">Plan: <strong>{selectedClub.business_info?.subscriptionPlan || 'Free'}</strong></span>
                  </div>
                </div>
                
                <div>
                  <Label>Precio de Instalación (€)</Label>
                  <Input
                    type="number"
                    placeholder="Basado en número de contactos"
                    value={approvalForm.installation_price}
                    onChange={(e) => setApprovalForm({...approvalForm, installation_price: e.target.value})}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Sugerido: {selectedClub.contacts_count} contactos
                  </p>
                </div>
                
                <div>
                  <Label>Notas del Admin (opcional)</Label>
                  <textarea
                    placeholder="Notas internas..."
                    value={approvalForm.admin_notes}
                    onChange={(e) => setApprovalForm({...approvalForm, admin_notes: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-xl resize-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={() => handleApprove(false)}
                  disabled={processing}
                  className="flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rechazar
                </Button>
                <Button
                  onClick={() => handleApprove(true)}
                  disabled={processing || !approvalForm.installation_price}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprobar
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBirthdayClubSection;
